// Catalog.ID Client — `mail_sign` sub-key certificates (v1)
// ============================================================================
// Normative format: apps/mail/docs/impl/envelope-wire.md §6, which adopts
//   apps/chat/docs/impl/chat-sign-certs.md VERBATIM with exactly five
//   substitutions (cert type, revocation type, keybag key id, key class,
//   key-grant mode). Everything else — field sets, the 1984/64/3373-byte
//   sizes, the 128-hex fingerprints, JCS rules, timestamp precision,
//   verification rules and their ORDER, verdict codes, revocation semantics,
//   the 60-day default / 90-day cap, and the §8 golden-vector doctrine — is
//   the chat spec unchanged.
// Parent plan: apps/mail/docs/plans/migration.md §5.2, §12 L1.
//
// `mail_sign` is TWO-LEVEL. An account-level ROOT lives in the CID keybag and
// signs nothing but CERTS for per-machine SUB-KEYS; the sub-keys sign mail
// envelopes (envelope-wire §2, `internal` kind).
//
// WHY A COPY OF chat-certs.ts RATHER THAN A SHARED CORE: the format is frozen
// either way, and envelope-wire §6 explicitly allows — but does not require —
// a later shared-core extraction. What must NOT be shared today is the domain
// separation: a mail root and a chat root are the same 1984-byte blob, and the
// TYPE STRING is what pins which profile a given signature belongs to. Two
// libraries with two type constants cannot accidentally cross-honour each
// other's certs; one library with a type parameter can.
//
// WHAT THIS MODULE DOES NOT DO — read this before trusting an `ok: true`:
// it verifies ONE link of the chain (envelope-signing sub-key → cert issued by
// this root). Whether the ROOT itself was valid at time `t` — its
// `registry_signing_key` claim-track window under the `mail_root` class — is
// the consumer's job (envelope-wire §4 step 5, L3's verification pipeline) and
// is deliberately out of scope. That is why `verifyCert` takes the root public
// key as a parameter instead of resolving it: resolution and root-validity
// live above this layer.
//
// Pure functions, no I/O, no module state. `async` only because the
// fingerprint helper is `crypto.subtle.digest`.
//
// TWO TRAPS THIS MODULE EXISTS TO CLOSE:
//   1. Wire form. `signHybrid` returns a JSON WRAPPER of the two signature
//      halves in separate base64 fields. Durable records — this one included —
//      carry the CONCATENATED 3373-byte form (`wrapperToCombinedBytes`),
//      base64-encoded once. The wrapper is never serialized here.
//   2. Sub-keys are hybrid, ALWAYS (migration §16, "hybrid sub-keys, always").
//      A cert whose `subkeyPubkey` is not exactly 1984 bytes is refused as a
//      FORMAT error, before signature verification — an Ed25519-only sub-key
//      "optimization" violates the durable-content rule and there is no
//      negotiation path.
// ============================================================================

import { canonicalize } from '@catalog-canonical-json';
import * as aes from './aes';
import {
    signHybrid, verifyHybrid, wrapperToCombinedBytes, combinedToWrapper, signingPublicKeyFromSeed,
    computeHybridSigningKeyFingerprint,
    HYBRID_PUBLIC_KEY_LEN, HYBRID_SECRET_SEED_LEN, HYBRID_COMBINED_SIGNATURE_LEN,
} from './signing';

// ── constants ───────────────────────────────────────────────────────────

export const MAIL_SIGN_CERT_TYPE = 'mail_sign_cert.v1';
export const MAIL_SIGN_REVOCATION_TYPE = 'mail_sign_cert_revocation.v1';

/** Inherited from chat M1 (migration §5.2): default cert lifetime. */
export const MAIL_CERT_DEFAULT_VALIDITY_DAYS = 60;
/** Inherited hard cap. Enforced at BOTH issuance and verification — a verifier
 *  that only checked the window would honour a ten-year cert minted by a buggy
 *  or coerced issuance path. */
export const MAIL_CERT_MAX_VALIDITY_DAYS = 90;

const MS_PER_DAY = 86_400_000;
const MAX_ACCOUNT_CID_LEN = 64;

/** ISO-8601 UTC, second precision. No offsets but `Z`, no fractional seconds,
 *  no space separator — a second accepted format would be a canonicalization
 *  hazard (chat spec §3.3, adopted). */
const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
const FINGERPRINT_RE = /^[0-9a-f]{128}$/;

const CERT_BODY_FIELDS = [
    'accountCid', 'issuedAt', 'rootFingerprint', 'subkeyPubkey', 'type',
    'validFrom', 'validTo',
] as const;
const REVOCATION_BODY_FIELDS = [
    'revokedAt', 'rootFingerprint', 'subkeyFingerprint', 'type',
] as const;

// ── types ───────────────────────────────────────────────────────────────

export interface MailSignCertBody {
    type: typeof MAIL_SIGN_CERT_TYPE;
    accountCid: string;
    /** 128 lowercase hex — SHA-512 of the root's 1984-byte combined pubkey. */
    rootFingerprint: string;
    /** base64; MUST decode to exactly 1984 bytes. */
    subkeyPubkey: string;
    validFrom: string;
    validTo: string;
    /** Informational: when the ceremony ran. Does NOT gate validity. */
    issuedAt: string;
}

/** The transported/stored record. Travels INLINE in every `internal`-kind
 *  envelope header (envelope-wire §2/§6) — there is no announcement channel
 *  and no cert appendix anywhere. The envelope itself is unsigned: a verifier
 *  re-canonicalizes `body`, so transit re-ordering or pretty-printing is
 *  harmless. */
export interface MailSignCert {
    body: MailSignCertBody;
    /** base64 of the COMBINED 3373-byte hybrid signature. Never a wrapper. */
    signature: string;
}

export interface MailSignRevocationBody {
    type: typeof MAIL_SIGN_REVOCATION_TYPE;
    rootFingerprint: string;
    subkeyFingerprint: string;
    revokedAt: string;
}

export interface MailSignCertRevocation {
    body: MailSignRevocationBody;
    signature: string;
}

export type CertVerifyFailureCode =
    | 'malformed'
    | 'bad-type'
    | 'bad-signature-encoding'
    | 'subkey-not-hybrid'
    | 'bad-timestamp'
    | 'bad-window'
    | 'lifetime-too-long'
    | 'bad-root-key'
    | 'root-mismatch'
    | 'bad-signature'
    | 'not-yet-valid'
    | 'expired'
    | 'revoked';

export type CertVerifyResult =
    | {
        ok: true;
        body: MailSignCertBody;
        /** The certified sub-key, decoded (1984 bytes). */
        subkeyPubkey: Uint8Array;
        /** 128 lowercase hex — SHA-512 of `subkeyPubkey`. */
        subkeyFingerprint: string;
        /** Revocations addressed to THIS root whose signature did not verify.
         *  Non-zero is an attack signal worth logging. */
        ignoredRevocations: number;
    }
    | {
        ok: false;
        code: CertVerifyFailureCode;
        message: string;
        ignoredRevocations: number;
    };

export type RevocationVerifyFailureCode =
    | 'malformed' | 'bad-type' | 'bad-signature-encoding' | 'bad-timestamp'
    | 'bad-root-key' | 'root-mismatch' | 'bad-signature';

export type RevocationVerifyResult =
    | { ok: true; body: MailSignRevocationBody }
    | { ok: false; code: RevocationVerifyFailureCode; message: string };

export interface IssueCertOptions {
    /** The issuing account's CID. */
    accountCid: string;
    /** 1…90; defaults to {@link MAIL_CERT_DEFAULT_VALIDITY_DAYS}. */
    validityDays?: number;
    /** Window start. Defaults to now. Truncated to second precision. */
    validFrom?: Date;
    /** Recorded as `issuedAt`. Defaults to now. */
    issuedAt?: Date;
}

export interface VerifyCertOptions {
    /** Evaluation instant. For archived mail this is the envelope's `signedAt`
     *  (envelope-wire §4 step 4), NOT `new Date()` — a years-old archive must
     *  verify against the window that was in force when it was signed. */
    at: Date;
    /** Candidate revocations. Each is signature-verified here; unverifiable
     *  ones are IGNORED, never honoured. */
    revocations?: readonly MailSignCertRevocation[];
}

// ── small helpers ───────────────────────────────────────────────────────

/** ISO-8601 UTC at second precision. */
export function toCertTimestamp(d: Date): string {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
        throw new Error('bad-timestamp');
    }
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function parseCertTimestamp(s: string): number | null {
    if (typeof s !== 'string' || !TIMESTAMP_RE.test(s)) return null;
    const ms = Date.parse(s);
    if (Number.isNaN(ms)) return null;
    // Round-trip guard: `2026-02-30T00:00:00Z` matches the shape and parses in
    // some engines by rolling over. Re-emitting must reproduce the input.
    if (new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z') !== s) return null;
    return ms;
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}

/** 128-lowercase-hex SHA-512 fingerprint of a 1984-byte hybrid signing key.
 *  Deliberately the SAME recipe chat uses — the fingerprint function is a
 *  property of the key type, not of the product. */
export async function subkeyFingerprintOf(publicKey: Uint8Array): Promise<string> {
    if (publicKey.length !== HYBRID_PUBLIC_KEY_LEN) throw new Error('subkey-not-hybrid');
    return toHexLower(await computeHybridSigningKeyFingerprint(publicKey));
}

function decodeBase64Exact(b64: string, expectedLen: number): Uint8Array | null {
    if (typeof b64 !== 'string' || b64.length === 0) return null;
    let bytes: Uint8Array;
    try {
        bytes = aes.fromBase64(b64);
    } catch {
        return null;
    }
    return bytes.length === expectedLen ? bytes : null;
}

/** Canonical (RFC 8785 / JCS) signing bytes for a cert body. Exported so
 *  cross-implementation golden vectors can pin them — the C# mail verifier
 *  (L3) must reproduce these bytes exactly. */
export function canonicalCertBytes(body: MailSignCertBody): Uint8Array {
    return canonicalize(body as unknown as { [k: string]: string });
}

/** Canonical (RFC 8785 / JCS) signing bytes for a revocation body. */
export function canonicalRevocationBytes(body: MailSignRevocationBody): Uint8Array {
    return canonicalize(body as unknown as { [k: string]: string });
}

function signCanonical(bytes: Uint8Array, rootSecretSeed: Uint8Array): string {
    return aes.toBase64(wrapperToCombinedBytes(signHybrid(bytes, rootSecretSeed)));
}

function certFail(
    code: CertVerifyFailureCode, message: string, ignoredRevocations = 0,
): CertVerifyResult {
    return { ok: false, code, message, ignoredRevocations };
}

// ── issuance ────────────────────────────────────────────────────────────

/**
 * Issue a cert binding `subkeyPubkey` to the account under the root held as
 * `rootSecretSeed`.
 *
 * `rootFingerprint` is derived from the seed we are about to SIGN with, never
 * from caller input: a caller-supplied fingerprint that disagreed with the
 * signing key would produce a cert failing the root-match rule with no useful
 * diagnosis.
 *
 * The caller's seed buffer is neither mutated nor retained. Zeroizing it is
 * the CALLER's job (it owns the buffer's lifetime — the key-grant handler and
 * the headless CLI both zero in a `finally`).
 *
 * Throws `Error` whose message is a stable code: `bad-root-seed`,
 * `bad-account-cid`, `subkey-not-hybrid`, `subkey-is-root`,
 * `validity-out-of-range`.
 */
export async function issueCert(
    rootSecretSeed: Uint8Array,
    subkeyPubkey: Uint8Array,
    opts: IssueCertOptions,
): Promise<MailSignCert> {
    if (!(rootSecretSeed instanceof Uint8Array) || rootSecretSeed.length !== HYBRID_SECRET_SEED_LEN) {
        throw new Error('bad-root-seed');
    }
    if (typeof opts.accountCid !== 'string' || opts.accountCid.length === 0
        || opts.accountCid.length > MAX_ACCOUNT_CID_LEN) {
        throw new Error('bad-account-cid');
    }
    // The issuer-side mirror of the verification-time hybrid check.
    if (!(subkeyPubkey instanceof Uint8Array) || subkeyPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        throw new Error('subkey-not-hybrid');
    }

    const days = opts.validityDays ?? MAIL_CERT_DEFAULT_VALIDITY_DAYS;
    if (!Number.isInteger(days) || days < 1 || days > MAIL_CERT_MAX_VALIDITY_DAYS) {
        throw new Error('validity-out-of-range');
    }

    const rootPubkey = signingPublicKeyFromSeed(rootSecretSeed);

    // A root that certifies itself as an envelope-signing sub-key collapses the
    // two levels and puts the account root on the hot send path — exactly what
    // the two-level model exists to prevent.
    if (equalBytes(rootPubkey, subkeyPubkey)) throw new Error('subkey-is-root');

    const validFrom = opts.validFrom ?? new Date();
    const issuedAt = opts.issuedAt ?? new Date();
    const validFromIso = toCertTimestamp(validFrom);
    // Derive validTo from the SECOND-TRUNCATED start, so the emitted window is
    // exactly `days` long rather than `days` minus a sub-second remainder.
    const validToIso = toCertTimestamp(new Date(Date.parse(validFromIso) + days * MS_PER_DAY));

    const body: MailSignCertBody = {
        type: MAIL_SIGN_CERT_TYPE,
        accountCid: opts.accountCid,
        rootFingerprint: toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey)),
        subkeyPubkey: aes.toBase64(subkeyPubkey),
        validFrom: validFromIso,
        validTo: validToIso,
        issuedAt: toCertTimestamp(issuedAt),
    };

    return { body, signature: signCanonical(canonicalCertBytes(body), rootSecretSeed) };
}

/**
 * Issue a revocation for a sub-key named by fingerprint.
 *
 * Naming the sub-key by fingerprint rather than by public key is deliberate:
 * this is a statement about a possibly-compromised key and there is no reason
 * to carry 2 KB of it. One revocation kills the fingerprint across EVERY cert
 * ever issued for it, future re-certifications included.
 *
 * Throws `Error('bad-root-seed')` / `Error('bad-subkey-fingerprint')`.
 */
export async function issueRevocation(
    rootSecretSeed: Uint8Array,
    subkeyFingerprint: string,
    revokedAt?: Date,
): Promise<MailSignCertRevocation> {
    if (!(rootSecretSeed instanceof Uint8Array) || rootSecretSeed.length !== HYBRID_SECRET_SEED_LEN) {
        throw new Error('bad-root-seed');
    }
    if (typeof subkeyFingerprint !== 'string' || !FINGERPRINT_RE.test(subkeyFingerprint)) {
        throw new Error('bad-subkey-fingerprint');
    }

    const rootPubkey = signingPublicKeyFromSeed(rootSecretSeed);
    const body: MailSignRevocationBody = {
        type: MAIL_SIGN_REVOCATION_TYPE,
        rootFingerprint: toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey)),
        subkeyFingerprint,
        revokedAt: toCertTimestamp(revokedAt ?? new Date()),
    };

    return { body, signature: signCanonical(canonicalRevocationBytes(body), rootSecretSeed) };
}

// ── verification ────────────────────────────────────────────────────────

/**
 * Verify a cert at instant `at`.
 *
 * Ordering: `root-mismatch` runs BEFORE `bad-signature` so "you handed me the
 * wrong root" stays distinguishable from "this is forged". The rest is
 * cheapest-first; the ML-DSA verify is by far the most expensive step and runs
 * last among the structural checks.
 *
 * Never throws on hostile input — every path returns a coded failure. Mail
 * verification failures must never drop mail silently (envelope-wire §4): the
 * caller materializes the message with the failed-verification verdict the UI
 * renders.
 */
export async function verifyCert(
    cert: unknown,
    rootPubkey: Uint8Array,
    opts: VerifyCertOptions,
): Promise<CertVerifyResult> {
    // Envelope + closed field set.
    const parsed = readCertEnvelope(cert);
    if (!parsed) return certFail('malformed', `not a ${MAIL_SIGN_CERT_TYPE} envelope`);
    const { body, signature } = parsed;

    // The type string is what pins the profile — and what stops a chat cert
    // being honoured here (and vice versa). There is no algorithm field, ever.
    if (body.type !== MAIL_SIGN_CERT_TYPE) {
        return certFail('bad-type', `expected type ${MAIL_SIGN_CERT_TYPE}, got ${body.type}`);
    }
    if (body.accountCid.length === 0 || body.accountCid.length > MAX_ACCOUNT_CID_LEN) {
        return certFail('malformed', 'accountCid must be 1..64 characters');
    }
    if (!FINGERPRINT_RE.test(body.rootFingerprint)) {
        return certFail('malformed', 'rootFingerprint must be 128 lowercase hex');
    }
    const signatureBytes = decodeBase64Exact(signature, HYBRID_COMBINED_SIGNATURE_LEN);
    if (!signatureBytes) {
        return certFail('bad-signature-encoding',
            `signature must be base64 of exactly ${HYBRID_COMBINED_SIGNATURE_LEN} bytes `
            + '(the combined hybrid form, never the wrapper JSON)');
    }
    // The hybrid-sub-keys-always trap, as a FORMAT refusal ahead of signature
    // checking.
    const subkeyPubkey = decodeBase64Exact(body.subkeyPubkey, HYBRID_PUBLIC_KEY_LEN);
    if (!subkeyPubkey) {
        return certFail('subkey-not-hybrid',
            `subkeyPubkey must be base64 of exactly ${HYBRID_PUBLIC_KEY_LEN} bytes `
            + '(Ed25519+ML-DSA-65 hybrid); non-hybrid sub-keys are refused');
    }
    const from = parseCertTimestamp(body.validFrom);
    const to = parseCertTimestamp(body.validTo);
    const issued = parseCertTimestamp(body.issuedAt);
    if (from === null || to === null || issued === null) {
        return certFail('bad-timestamp',
            'validFrom/validTo/issuedAt must be ISO-8601 UTC at second precision');
    }
    if (to <= from) return certFail('bad-window', 'validTo must be strictly after validFrom');
    if (to - from > MAIL_CERT_MAX_VALIDITY_DAYS * MS_PER_DAY) {
        return certFail('lifetime-too-long',
            `cert lifetime exceeds the ${MAIL_CERT_MAX_VALIDITY_DAYS}-day cap`);
    }
    if (!(rootPubkey instanceof Uint8Array) || rootPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return certFail('bad-root-key',
            `root public key must be ${HYBRID_PUBLIC_KEY_LEN} bytes`);
    }
    // Before the signature check, deliberately.
    const rootFingerprint = toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey));
    if (rootFingerprint !== body.rootFingerprint) {
        return certFail('root-mismatch',
            'cert names a different root than the key supplied for verification');
    }
    // Both halves; verifyHybrid computes each into its own variable.
    if (!verifyHybrid(canonicalCertBytes(body), combinedToWrapper(signatureBytes), rootPubkey)) {
        return certFail('bad-signature', 'hybrid signature does not verify under the root key');
    }

    // Window — inclusive at both ends.
    const at = opts.at instanceof Date ? opts.at.getTime() : NaN;
    if (Number.isNaN(at)) return certFail('bad-timestamp', 'evaluation instant `at` is not a Date');
    if (at < from) return certFail('not-yet-valid', `cert is not valid until ${body.validFrom}`);
    if (at > to) return certFail('expired', `cert expired at ${body.validTo}`);

    // Revocations, each authenticated before it is honoured.
    const subkeyFingerprint = toHexLower(await computeHybridSigningKeyFingerprint(subkeyPubkey));
    let ignoredRevocations = 0;
    for (const candidate of opts.revocations ?? []) {
        const shape = readRevocationEnvelope(candidate);
        // Not addressed to this root — not our business, and not an attack
        // signal either. Silently skipped, NOT counted as ignored.
        if (!shape || shape.body.rootFingerprint !== rootFingerprint) continue;

        const verified = await verifyRevocation(candidate, rootPubkey);
        if (!verified.ok) {
            // A forged revocation must NEVER kill a key: anyone who can inject
            // bytes into a delivered envelope would otherwise be able to
            // disable an arbitrary device.
            ignoredRevocations++;
            continue;
        }
        if (verified.body.subkeyFingerprint !== subkeyFingerprint) continue;
        const revokedAt = parseCertTimestamp(verified.body.revokedAt);
        if (revokedAt === null) { ignoredRevocations++; continue; }
        // Effective from `revokedAt` ONWARD — not retroactive. Signatures made
        // earlier stay valid, which is what keeps archived mail verifiable.
        if (revokedAt <= at) {
            return certFail('revoked',
                `sub-key was revoked at ${verified.body.revokedAt}`, ignoredRevocations);
        }
    }

    return { ok: true, body, subkeyPubkey, subkeyFingerprint, ignoredRevocations };
}

/**
 * Verify a revocation's own signature under the root that supposedly issued it.
 * Callers MUST do this before honouring one; `verifyCert` does it internally
 * for everything on its `revocations` list.
 */
export async function verifyRevocation(
    revocation: unknown,
    rootPubkey: Uint8Array,
): Promise<RevocationVerifyResult> {
    const parsed = readRevocationEnvelope(revocation);
    if (!parsed) {
        return { ok: false, code: 'malformed', message: `not a ${MAIL_SIGN_REVOCATION_TYPE} envelope` };
    }
    const { body, signature } = parsed;

    if (body.type !== MAIL_SIGN_REVOCATION_TYPE) {
        return { ok: false, code: 'bad-type', message: `expected type ${MAIL_SIGN_REVOCATION_TYPE}` };
    }
    if (!FINGERPRINT_RE.test(body.rootFingerprint) || !FINGERPRINT_RE.test(body.subkeyFingerprint)) {
        return { ok: false, code: 'malformed', message: 'fingerprints must be 128 lowercase hex' };
    }
    if (parseCertTimestamp(body.revokedAt) === null) {
        return { ok: false, code: 'bad-timestamp', message: 'revokedAt must be ISO-8601 UTC at second precision' };
    }
    const signatureBytes = decodeBase64Exact(signature, HYBRID_COMBINED_SIGNATURE_LEN);
    if (!signatureBytes) {
        return {
            ok: false, code: 'bad-signature-encoding',
            message: `signature must be base64 of exactly ${HYBRID_COMBINED_SIGNATURE_LEN} bytes`,
        };
    }
    if (!(rootPubkey instanceof Uint8Array) || rootPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return { ok: false, code: 'bad-root-key', message: 'root public key must be 1984 bytes' };
    }
    const rootFingerprint = toHexLower(await computeHybridSigningKeyFingerprint(rootPubkey));
    if (rootFingerprint !== body.rootFingerprint) {
        return {
            ok: false, code: 'root-mismatch',
            message: 'revocation names a different root than the key supplied',
        };
    }
    if (!verifyHybrid(canonicalRevocationBytes(body), combinedToWrapper(signatureBytes), rootPubkey)) {
        return { ok: false, code: 'bad-signature', message: 'hybrid signature does not verify' };
    }
    return { ok: true, body };
}

// ── envelope readers (closed field set, all-strings) ────────────────────

function readEnvelope(value: unknown): { body: Record<string, unknown>; signature: string } | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const v = value as { body?: unknown; signature?: unknown };
    if (!v.body || typeof v.body !== 'object' || Array.isArray(v.body)) return null;
    if (typeof v.signature !== 'string') return null;
    return { body: v.body as Record<string, unknown>, signature: v.signature };
}

/**
 * Closed field set: exactly the cert-body keys, every value a string.
 *
 * A signature over canonicalized bytes already fails when a field is injected
 * after signing — JCS commits to every key. This additionally refuses a
 * SIGNER who invents fields, so producers and consumers cannot drift into a
 * de-facto v1.5.
 */
function readCertEnvelope(value: unknown): { body: MailSignCertBody; signature: string } | null {
    const env = readEnvelope(value);
    if (!env) return null;
    if (!hasExactStringFields(env.body, CERT_BODY_FIELDS)) return null;
    return { body: env.body as unknown as MailSignCertBody, signature: env.signature };
}

function readRevocationEnvelope(
    value: unknown,
): { body: MailSignRevocationBody; signature: string } | null {
    const env = readEnvelope(value);
    if (!env) return null;
    if (!hasExactStringFields(env.body, REVOCATION_BODY_FIELDS)) return null;
    return { body: env.body as unknown as MailSignRevocationBody, signature: env.signature };
}

function hasExactStringFields(
    body: Record<string, unknown>, fields: readonly string[],
): boolean {
    const keys = Object.keys(body);
    if (keys.length !== fields.length) return false;
    for (const field of fields) {
        if (typeof body[field] !== 'string') return false;
    }
    return true;
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
}

// ── serialization ───────────────────────────────────────────────────────
//
// Provided so callers never hand-roll the envelope. The output is ordinary
// JSON, NOT canonical JSON — the canonical form is the SIGNED form of `body`
// only, and a verifier re-derives it. Keeping the two distinct is what makes
// transit re-ordering harmless, which matters here because the cert rides
// inline inside a JCS-canonicalized envelope header (envelope-wire §2).

export function certToJson(cert: MailSignCert): string {
    return JSON.stringify(cert);
}

export function parseCert(json: string): MailSignCert | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return null;
    }
    const env = readCertEnvelope(parsed);
    return env ? { body: env.body, signature: env.signature } : null;
}

export function revocationToJson(revocation: MailSignCertRevocation): string {
    return JSON.stringify(revocation);
}

export function parseRevocation(json: string): MailSignCertRevocation | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return null;
    }
    const env = readRevocationEnvelope(parsed);
    return env ? { body: env.body, signature: env.signature } : null;
}
